import React, { useMemo, useRef, useState, useCallback } from 'react';
import ForceGraph2D from 'react-force-graph-2d';
import { AnimatePresence, motion } from 'framer-motion';
import useMedicationSelectionStore from './medicationSelectionStore';

const MedicationGalaxy = ({ patientData, drugInteractions }) => {
  const fgRef = useRef(null);
  const [focusedNodeId, setFocusedNodeId] = useState(null);
  const [tooltip, setTooltip] = useState(null);
  const setActiveDrug = useMedicationSelectionStore((state) => state.setActiveDrug);

  const data = useMemo(() => {
    if (!patientData?.medications) {
      return { nodes: [], links: [] };
    }

    const meds = patientData.medications;
    const dosageMagnitude = (dose) => {
      if (!dose) return 1;
      const match = dose.match(/([\d.]+)/);
      if (!match) return 1;
      const value = parseFloat(match[1]);
      if (Number.isNaN(value)) return 1;
      return Math.min(Math.max(value / 5, 2), 18);
    };

    const parseDoseMg = (dose) => {
      if (!dose) return null;
      const match = dose.match(/([\d.]+)/);
      if (!match) return null;
      const value = parseFloat(match[1]);
      if (Number.isNaN(value)) return null;
      return value;
    };

    const nodesMap = new Map();
    meds.forEach((med, index) => {
      const id = med.generic_name || `med-${index}`;
      if (!nodesMap.has(id)) {
        nodesMap.set(id, {
          id,
          label: id,
          dose: med.dose || '',
          baseVal: dosageMagnitude(med.dose),
          doseMg: parseDoseMg(med.dose),
        });
      }
    });

    const links = [];
    const centerWeights = new Map();
    const drugMeta = new Map();

    if (Array.isArray(drugInteractions)) {
      drugInteractions.forEach((interaction) => {
        const a = interaction.drug_a;
        const b = interaction.drug_b;
        if (!a || !b) return;
        if (!nodesMap.has(a)) {
          nodesMap.set(a, { id: a, label: a, dose: '', baseVal: 4 });
        }
        if (!nodesMap.has(b)) {
          nodesMap.set(b, { id: b, label: b, dose: '', baseVal: 4 });
        }

        const severityLabel = interaction.severity || '';
        const severity = severityLabel.toLowerCase();
        const isHigh = severity === 'major' || severity === 'high';
        // Neo colors: danger (#ff6b6b) vs orange/warning (#ff9f43)
        const linkColor = isHigh ? '#ff6b6b' : '#ff9f43';

        links.push({
          source: a,
          target: b,
          color: linkColor,
          severity,
        });

        if (isHigh) {
          centerWeights.set(a, (centerWeights.get(a) || 0) + 1);
          centerWeights.set(b, (centerWeights.get(b) || 0) + 1);
        }

        const severityRank =
          severity === 'major' || severity === 'high'
            ? 3
            : severity === 'moderate'
            ? 2
            : severity === 'minor' || severity === 'low'
            ? 1
            : 0;

        const warningText =
          interaction.clinical_interpretation ||
          interaction.effect ||
          interaction.description ||
          'Interaction risk increased';

        [a, b].forEach((drugId) => {
          const existing = drugMeta.get(drugId);
          if (!existing || severityRank > existing.rank) {
            drugMeta.set(drugId, {
              rank: severityRank,
              severity: severityLabel,
              warning: warningText,
            });
          }
        });
      });
    }

    const nodes = Array.from(nodesMap.values()).map((node) => {
      const weight = centerWeights.get(node.id) || 0;
      const meta = drugMeta.get(node.id) || {};
      return {
        ...node,
        centerWeight: weight,
        riskSeverity: meta.severity || null,
        warning: meta.warning || null,
      };
    });

    return { nodes, links };
  }, [patientData, drugInteractions]);

  const nodeRelSize = 4;

  const getNodeColor = useCallback(
    (node) => {
      const hasInteractions = data.links.some(
        (l) => l.source.id === node.id || l.target.id === node.id
      );
      if (!hasInteractions) {
        return 'rgba(148, 163, 184, 0.9)';
      }
      const maxWeight = Math.max(
        1,
        ...data.nodes.map((n) => n.centerWeight || 0)
      );
      const intensity = (node.centerWeight || 0) / maxWeight;
      // Neo accent (#0984e3) mixed with neo-main (#2d3436)
      // Or just return specific neo colors based on weight
      if (intensity > 0.7) return '#ff6b6b'; // danger
      if (intensity > 0.3) return '#ff9f43'; // warning
      return '#0984e3'; // accent
    },
    [data]
  );

  const handleNodeHover = useCallback(
    (node) => {
      setFocusedNodeId(node ? node.id : null);
      if (node) {
        setActiveDrug({
          id: node.id,
          label: node.label,
          dose: node.dose,
          doseMg: node.doseMg || null,
          riskSeverity: node.riskSeverity || null,
          warning: node.warning || null,
        });
      }
    },
    [setActiveDrug]
  );

  const handleNodeClick = useCallback(
    (node) => {
      if (!node || !fgRef.current) return;
      const activePayload = {
        id: node.id,
        label: node.label,
        dose: node.dose,
        doseMg: node.doseMg || null,
        riskSeverity: node.riskSeverity || null,
        warning: node.warning || null,
      };
      setActiveDrug(activePayload);

      const graph = fgRef.current;
      const coords = graph.graph2ScreenCoords(node.x, node.y);
      setTooltip({
        x: coords.x,
        y: coords.y,
        drugLabel: node.label,
        warning: node.warning || activePayload.warning,
        severity:
          node.riskSeverity ||
          (node.centerWeight && node.centerWeight > 0
            ? 'Interaction'
            : 'No issues detected'),
      });
    },
    [setActiveDrug]
  );

  const drawNode = useCallback(
    (node, ctx, globalScale) => {
      const label = node.label;
      const fontSize = 12 / globalScale;
      const baseRadius = (node.baseVal || 6) * (nodeRelSize / 6);

      const isFocused = focusedNodeId === node.id;
      const isNeighbor =
        focusedNodeId &&
        data.links.some(
          (l) =>
            (l.source.id === focusedNodeId && l.target.id === node.id) ||
            (l.target.id === focusedNodeId && l.source.id === node.id)
        );

      const dim =
        focusedNodeId && !isFocused && !isNeighbor ? 0.12 : 1;

      const color = getNodeColor(node);
      ctx.globalAlpha = dim;
      ctx.beginPath();
      ctx.arc(node.x, node.y, baseRadius, 0, 2 * Math.PI, false);
      ctx.fillStyle = color;
      ctx.fill();

      if (isFocused) {
        ctx.lineWidth = 2.5 / globalScale;
        ctx.strokeStyle = '#2d3436'; // neo-main
        ctx.stroke();
      }

      ctx.globalAlpha = dim;
      ctx.font = `700 ${fontSize}px 'Public Sans', sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      ctx.fillStyle = '#2d3436'; // neo-main
      ctx.fillText(label, node.x, node.y + baseRadius + 4 / globalScale);
    },
    [data, focusedNodeId, getNodeColor]
  );

  const drawLink = useCallback(
    (link, ctx) => {
      const isFocused =
        focusedNodeId &&
        ((link.source.id === focusedNodeId ||
          link.target.id === focusedNodeId));

      const dim = focusedNodeId && !isFocused ? 0.08 : 0.8;

      ctx.globalAlpha = dim;
      ctx.strokeStyle = link.color || '#b2bec3';
      ctx.lineWidth =
        (link.severity === 'major' || link.severity === 'high') ? 2.5 : 1.5;
      ctx.beginPath();
      ctx.moveTo(link.source.x, link.source.y);
      ctx.lineTo(link.target.x, link.target.y);
      ctx.stroke();
    },
    [focusedNodeId]
  );

  const linkDirectionalParticleWidth = (link) =>
    link.severity === 'major' || link.severity === 'high' ? 3 : 0;

  const linkDirectionalParticles = (link) =>
    link.severity === 'major' || link.severity === 'high' ? 2 : 0;

  const coolDownTicks = 150;
  const onEngineStop = () => {
    if (!fgRef.current) return;
    const graph = fgRef.current;
    const highCenter = data.nodes
      .filter((n) => (n.centerWeight || 0) > 0)
      .sort((a, b) => (b.centerWeight || 0) - (a.centerWeight || 0))[0];

    if (highCenter) {
      graph.zoomToFit(400, 60, (node) => node.id === highCenter.id);
    }
  };

  return (
    <motion.div
      className="relative flex-1 min-h-[300px] rounded-xl bg-white border-2 border-neo-main shadow-neo-brutal overflow-hidden"
      initial={{ opacity: 0, scale: 0.94, y: 24 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ duration: 0.9, ease: 'easeOut' }}
      whileHover={{ scale: 1.01, boxShadow: '8px 8px 0px 0px rgba(0,0,0,1)' }}
    >
      <motion.div
        className="absolute inset-0 bg-neo-bg pointer-events-none opacity-50"
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.5 }}
        transition={{ duration: 1.2, ease: 'easeOut' }}
      />
      <ForceGraph2D
        ref={fgRef}
        graphData={data}
        nodeLabel="label"
        nodeRelSize={nodeRelSize}
        onNodeHover={handleNodeHover}
        onNodeClick={handleNodeClick}
        cooldownTicks={coolDownTicks}
        onEngineStop={onEngineStop}
        nodeCanvasObject={drawNode}
        linkCanvasObject={drawLink}
        linkDirectionalParticles={linkDirectionalParticles}
        linkDirectionalParticleWidth={linkDirectionalParticleWidth}
        backgroundColor="transparent"
      />
      <motion.div
        className="absolute top-3 right-4 text-xs font-black uppercase tracking-widest text-neo-text-sub"
        initial={{ opacity: 0, x: 12 }}
        animate={{ opacity: 0.9, x: 0 }}
        transition={{ duration: 0.7, ease: 'easeOut', delay: 0.3 }}
      >
        Interaction Galaxy
      </motion.div>
      <motion.div
        className="absolute bottom-3 left-4 text-xs text-neo-text font-bold flex flex-col gap-0.5 bg-white/80 p-2 rounded border-2 border-neo-main"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 0.9, y: 0 }}
        transition={{ duration: 0.7, ease: 'easeOut', delay: 0.4 }}
      >
        <span>• Node size ∝ dosage magnitude</span>
        <span>• Warm hubs = highest priority drugs</span>
        <span>• Click a node for focused interaction summary</span>
      </motion.div>
      <AnimatePresence>
        {tooltip && (
          <motion.div
            className="absolute z-20 max-w-xs rounded-lg bg-white border-2 border-neo-main shadow-neo-brutal px-4 py-3"
            initial={{ opacity: 0, scale: 0.8, y: -8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: -4 }}
            transition={{ type: 'spring', stiffness: 260, damping: 20 }}
            style={{
              left: tooltip.x + 16,
              top: tooltip.y - 24,
            }}
          >
            <p className="text-xs font-black uppercase tracking-widest text-neo-danger mb-1">
              {tooltip.severity || 'Interaction Warning'}
            </p>
            <p className="text-sm font-black text-neo-main mb-1">
              {tooltip.drugLabel}
            </p>
            <p className="text-xs text-neo-text font-medium leading-snug">
              {tooltip.warning}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default MedicationGalaxy;
